import React, { useState, useMemo } from "react";
import { sendPortalMessage, sendReply } from "../services/api";
import EmailAutocomplete from "./EmailAutocomplete";
import { mailErrorMessage } from "../utils/mailErrorMessage";
import { ArrowLeftIcon, PaperclipIcon, SendIcon } from "./icons/InterfaceIcons";
import { generateUuid } from "../utils/clientId";
import { isPortalConversation } from "../utils/commandeMail";

export default function ReplyMailModal({ commande, onClose, contained = false }) {
    const idempotencyKey = useMemo(() => generateUuid(), []);
    const portalConversation = isPortalConversation(commande);

    const initialRecipients = commande.emailEmetteur ? [commande.emailEmetteur] : [];
    const [recipients, setRecipients] = useState(initialRecipients);
    const [sujet, setSujet] = useState(`Re: ${commande.objet || commande.commentaire || ""}`);
    const [contenu, setContenu] = useState("");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    const addRecipient = (email) => {
        if (!email || recipients.includes(email)) return;
        setRecipients(prev => [...prev, email]);
    };

    const removeRecipient = (index) => {
        setRecipients(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileChange = (e) => {
        setFiles(prev => [...prev, ...Array.from(e.target.files)]);
        e.target.value = "";
    };

    const handleSend = async () => {
        if (!contenu.trim() || (!portalConversation && (!sujet.trim() || recipients.length === 0))) return;
        setSending(true);
        setError(null);
        try {
            if (portalConversation) {
                await sendPortalMessage(commande.id, contenu, idempotencyKey);
            } else {
                const extraRecipients = recipients.filter(r => r !== commande.emailEmetteur);
                await sendReply(commande.id, sujet, contenu, extraRecipients, files, idempotencyKey);
            }
            onClose(true);
        } catch (e) {
            const message = mailErrorMessage(e);
            setError(portalConversation && message === "Erreur lors de l'envoi du mail."
                ? "Erreur lors de l'envoi du message."
                : message);
            setSending(false);
        }
    };

    return (
        <div className={`reply-mail-overlay${contained ? " reply-mail-overlay--contained" : ""}`} role="presentation" style={{
            position: contained ? "absolute" : "fixed", inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000
        }}>
            <div className={`reply-mail-dialog${contained ? " reply-mail-dialog--contained" : ""}`} role="dialog" aria-modal="true" aria-label={portalConversation ? "Répondre à l'EHPAD" : "Répondre au mail"} style={{
                background: "white",
                borderRadius: contained ? 0 : "12px",
                width: contained ? "100%" : "560px",
                maxWidth: contained ? "none" : "95vw",
                height: contained ? "100%" : "auto",
                maxHeight: contained ? "none" : "90vh",
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}>
                {/* Header */}
                <div className="reply-mail-header" style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex", alignItems: "center", justifyContent: "space-between"
                }}>
                    <div>
                        <span className="reply-mail-eyebrow">Nouveau message</span>
                        <h2 className="reply-mail-title">{portalConversation ? "Répondre à l'EHPAD" : "Répondre au mail"}</h2>
                    </div>
                    {contained ? (
                        <button className="reply-mail-back" type="button" onClick={() => onClose(false)}>
                            <ArrowLeftIcon size={17} />
                            Retour au mail
                        </button>
                    ) : (
                        <button className="reply-mail-close" aria-label="Fermer la réponse" onClick={() => onClose(false)}>✕</button>
                    )}
                </div>

                {/* Body */}
                <div className="reply-mail-body" style={{ padding: "16px 20px", display: "flex", flex: contained ? 1 : "initial", minHeight: 0, flexDirection: "column", gap: "12px", overflowY: "auto" }}>

                    {portalConversation && (
                        <div style={{ padding: "10px 12px", borderRadius: "7px", background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", fontSize: "12px" }}>
                            Ce message sera publié dans la conversation du portail EHPAD.
                        </div>
                    )}

                    {/* Destinataires */}
                    {!portalConversation && <div className="reply-mail-field">
                        <div className="reply-mail-label" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Destinataires <span>*</span></div>
                        <div className="reply-mail-recipients" style={{
                            display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center",
                            border: "1px solid #cbd5e1", borderRadius: "7px",
                            padding: "6px 8px", minHeight: "40px",
                        }}>
                            {recipients.map((r, i) => (
                                <span className="reply-mail-recipient" key={i} style={{
                                    display: "inline-flex", alignItems: "center", gap: "4px",
                                    background: "#eff6ff", border: "1px solid #bfdbfe",
                                    borderRadius: "4px", padding: "2px 6px",
                                    fontSize: "12px", color: "#1e40af",
                                }}>
                                    {r}
                                    <button className="reply-mail-recipient-remove" aria-label={`Retirer ${r}`} onClick={() => removeRecipient(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#93c5fd", fontSize: "14px", lineHeight: 1, padding: 0 }}>×</button>
                                </span>
                            ))}
                            <EmailAutocomplete
                                onSelect={addRecipient}
                                exclude={recipients}
                                placeholder={recipients.length === 0 ? "Ajouter un destinataire..." : ""}
                            />
                        </div>
                    </div>}

                    {!portalConversation && <input
                        className="reply-mail-subject"
                        value={sujet}
                        onChange={e => setSujet(e.target.value)}
                        placeholder="Objet"
                        style={{ padding: "8px 12px", borderRadius: "7px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", color: "#1e293b" }}
                    />}

                    <textarea
                        className="reply-mail-message"
                        value={contenu}
                        onChange={e => setContenu(e.target.value)}
                        placeholder="Votre message..."
                        rows={8}
                        style={{ padding: "10px 12px", borderRadius: "7px", border: "1px solid #cbd5e1", fontSize: "13px", resize: "vertical", outline: "none", color: "#1e293b", lineHeight: "1.6", fontFamily: "inherit" }}
                    />

                    {/* Pièces jointes */}
                    {!portalConversation && <div className="reply-mail-attachments">
                        <label className="reply-mail-add-attachment" style={{ display: "inline-block", padding: "5px 12px", borderRadius: "7px", border: "1px dashed #93c5fd", background: "#f0f9ff", color: "#2563eb", fontSize: "12px", cursor: "pointer" }}>
                            <PaperclipIcon size={16} />
                            Ajouter une pièce jointe
                            <input type="file" multiple style={{ display: "none" }} onChange={handleFileChange} />
                        </label>
                        {files.length > 0 && (
                            <div className="reply-mail-attachment-list" style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
                                {files.map((f, i) => (
                                    <div className="reply-mail-attachment" key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 10px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "7px", fontSize: "12px" }}>
                                        <PaperclipIcon size={15} />
                                        <span className="reply-mail-attachment-name" style={{ color: "#166534", fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                                        <span className="reply-mail-attachment-size" style={{ color: "#4ade80", whiteSpace: "nowrap" }}>
                                            {f.size < 1024 ? `${f.size} o` : f.size < 1048576 ? `${(f.size / 1024).toFixed(1)} Ko` : `${(f.size / 1048576).toFixed(1)} Mo`}
                                        </span>
                                        <button className="reply-mail-attachment-remove" aria-label={`Retirer ${f.name}`} onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#86efac", fontSize: "14px", padding: 0, lineHeight: 1 }}>✕</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>}

                    {error && <div className="reply-mail-error" role="alert" style={{ color: "#dc2626", fontSize: "13px" }}>{error}</div>}
                </div>

                {/* Footer */}
                <div className="reply-mail-footer" style={{ padding: "12px 20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button
                        className="reply-mail-cancel"
                        onClick={() => onClose(false)}
                        disabled={sending}
                        style={{ padding: "7px 16px", borderRadius: "7px", border: "1px solid #cbd5e1", background: "white", color: "#475569", fontSize: "13px", cursor: "pointer" }}
                    >
                        Annuler
                    </button>
                    <button
                        className="reply-mail-send"
                        onClick={handleSend}
                        disabled={sending || !contenu.trim() || (!portalConversation && (!sujet.trim() || recipients.length === 0))}
                        style={{
                            padding: "7px 16px", borderRadius: "7px", border: "none",
                            background: (sending || (!portalConversation && recipients.length === 0)) ? "#93c5fd" : "#2563eb",
                            color: "white", fontSize: "13px", fontWeight: 600,
                            cursor: (sending || (!portalConversation && recipients.length === 0)) ? "not-allowed" : "pointer"
                        }}
                    >
                        {!sending && <SendIcon size={16} />}
                        {sending ? "Envoi..." : (portalConversation ? "Envoyer" : `Envoyer (${recipients.length})`)}
                    </button>
                </div>
            </div>
        </div>
    );
}
